/**
 * Store Bypass Handler
 * 
 * Automatically selects Aquila (Store 1) and bypasses store selection when enabled.
 * Reuses existing store selection logic for consistency and reliability.
 */

import { updateSelectedStore, clearCurrentTimeSlotOrders, updateCurrentTimeSlotOrders } from '../components/kit/Redux/Actions';
import { updateUserStore, getStoreSpecificOrders } from '../db/db';
import { PATH_PRODUCT_TYPE, PATH_ORDER_MANAGEMENT } from '../constants/Paths';

// Aquila store configuration (Store 1)
const AQUILA_STORE = {
  id: 1,
  code: 'aquila',
  name: 'Aquila World',
  title: 'Aquila World', // Used for button click handler compatibility
  descr: '⚔️ Aquila World', // Display format
  emoji: '⚔️'
};

/**
 * Auto-select Aquila store and handle navigation
 * Replicates the logic from StoreSelectionScreen.onStoreSelect()
 */
export const bypassToAquilaStore = async (sessionToken, userProfile, dispatch, navigate) => {
  try {
    console.log('[StoreBypass] Automatically selecting Aquila store (ID: 1)');
    
    // Update user's store selection in database
    await updateUserStore(
      sessionToken,
      userProfile.userid,
      AQUILA_STORE.id
    );

    // Update Redux store with Aquila selection
    dispatch(
      updateSelectedStore({
        storeId: AQUILA_STORE.id,
        storeCode: AQUILA_STORE.code,
        storeName: AQUILA_STORE.name,
        storeEmoji: AQUILA_STORE.emoji,
      })
    );

    // Clear existing orders since we're setting store context
    dispatch(clearCurrentTimeSlotOrders());

    // Check for store-specific orders in Aquila
    console.log(`[StoreBypass] Checking for orders in Aquila store for user ${userProfile.userid}`);
    const storeOrders = await getStoreSpecificOrders(
      sessionToken,
      AQUILA_STORE.id,
      userProfile.userid
    );

    console.log(`[StoreBypass] Store-specific orders found:`, storeOrders);

    // Update current time slot orders with store-specific data
    dispatch(updateCurrentTimeSlotOrders(storeOrders));

    // Navigate based on whether orders exist for Aquila store
    if (storeOrders && Object.keys(storeOrders).length > 0) {
      console.log(`[StoreBypass] Navigating to Order Management - found orders for Aquila`);
      navigate(PATH_ORDER_MANAGEMENT, { replace: true });
    } else {
      console.log(`[StoreBypass] Navigating to Product Type - no orders found for Aquila`);
      navigate(PATH_PRODUCT_TYPE, { replace: true });
    }

    return true; // Success
  } catch (error) {
    console.error('[StoreBypass] Error auto-selecting Aquila store:', error);
    throw error;
  }
};

/**
 * Check if user should bypass store selection and auto-select Aquila
 */
export const shouldBypassStoreSelection = (featureFlag) => {
  return featureFlag === true;
};

/**
 * Get Aquila store configuration
 */
export const getAquilaStoreConfig = () => {
  return { ...AQUILA_STORE };
};

export default {
  bypassToAquilaStore,
  shouldBypassStoreSelection,
  getAquilaStoreConfig,
};